package com.htmlagent.site

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.htmlagent.site.theme.Fonts
import com.htmlagent.site.theme.NavHeight
import com.htmlagent.site.theme.SAFE_AREA_TOP
import com.htmlagent.site.theme.Slate
import com.htmlagent.site.theme.Space
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.App
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.InstallPrompt
import com.htmlagent.site.state.ThemePreference
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.layer.SilkLayer
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div

/**
 * Initial color mode: the person's explicit choice, or the system's when they left it on auto.
 *
 * It gets its own `@InitSilk`: there is no guaranteed order between them, so none of them can
 * rely on another.
 */
@InitSilk
fun initColorMode(ctx: InitSilkContext) {
    ctx.config.initialColorMode = ThemePreference.current.value.resolve(ColorMode.systemPreference)
}

/** Starts listening for the browser's install offer before anything renders; see [InstallPrompt]. */
@InitSilk
@Suppress("UnusedParameter") // `@InitSilk` requires the context; this init needs nothing from it.
fun initInstallPrompt(ctx: InitSilkContext) {
    InstallPrompt.listen()
}

/**
 * Applies the system tokens to the Silk widgets (family, font size, radius, line).
 *
 * Height is left alone: Silk pins the size with its own class (`silk-button-size_md`) and beats
 * any CSS on top, so each call picks the size with `ButtonSize`. It uses `modifyStyleBase` rather
 * than `replace` so as not to depend on the widget's internals.
 */
@InitSilk
fun initWidgetSizes(ctx: InitSilkContext) {
    ctx.theme.modifyStyleBase(ButtonStyle) {
        Modifier.styleModifier {
            property("font-family", Tokens.fontSans.value())
            property("border-radius", Tokens.radiusMd.value())
        }
    }

    ctx.theme.modifyStyleBase(InputStyle) {
        Modifier.styleModifier {
            property("font-family", Tokens.fontSans.value())
            property("font-size", Tokens.textBase.value())
            property("border-radius", Tokens.radiusMd.value())
        }
    }

    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.styleModifier {
            property("border-color", Tokens.line.value())
            property("width", "100%")
        }
    }
}

/**
 * The global styles that hang off no component: `body` and `#root` reset, inherited typography
 * on controls, keyframes and reduced motion.
 *
 * Without the reset the application is painted inside a `body` with the browser's margins and the
 * content's height, and the panels end up floating in a white band.
 *
 * **They go in Silk's `base` layer, and that is not cosmetic.** A style registered on the raw
 * stylesheet is unlayered, and unlayered CSS beats every layer no matter how specific the loser
 * is — so `button { color: inherit }` was overriding the `color` of every button style in the
 * project, variants included. The symptom was a solid button whose label took the page's text
 * color instead of its own: near-black on the saturated accent, 2.70:1, below the 4.5:1 floor. Silk
 * reserves `base` for exactly this, «global styles that anything else should override».
 *
 * The reduced-motion rule below deliberately stays **out** of it: that one is meant to win.
 *
 * The keyframes go in as raw CSS and not through the Silk API: the name that API generates
 * depends on the property name, and renaming it would leave the stylesheet pointing at nothing
 * with no error at all. The reduced motion rule neutralizes them on its own, and also covers
 * any animation added later.
 *
 * `ha-glow` animates opacity and scale rather than a `box-shadow` color because a keyframe
 * written as raw text cannot read a token: what breathes is a blurred accent-filled element
 * behind the piece, and that element takes its color from the system like everything else.
 */
@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.layer(SilkLayer.BASE.layerName) {
        registerStyleBase("html, body") {
            Modifier.styleModifier {
                property("margin", "0")
                property("padding", "0")
                property("height", "100%")
                // Scheme only: the canvas color comes from the shell tokens.
                property("color-scheme", "light dark")
                property("font-family", Fonts.SansCss)
                // Without this, a field with `width: 100%` overflows its column.
                property("box-sizing", "border-box")
                // The landing navigates by anchor. Smooth is the document scroller only -the
                // property is not inherited- so the chat log, which sets scrollTop itself, is
                // untouched; and the padding keeps a heading from landing under the sticky bar.
                property("scroll-behavior", "smooth")
                property("scroll-padding-top", "calc($NavHeight + $SAFE_AREA_TOP + ${Space.Md})")
            }
        }

        // Chrome on Android paints the gesture-navigation bar with the root's background, and a
        // transparent root left a white strip under the page. The tokens live on the shell, not
        // on `<html>`, so the canvas step is read from the scale for each mode Silk marks there.
        registerStyleBase("html.silk-light") { Modifier.backgroundColor(Slate.Light.S1) }
        registerStyleBase("html.silk-dark") { Modifier.backgroundColor(Slate.Dark.S1) }

        registerStyleBase("*, *::before, *::after") {
            Modifier.styleModifier { property("box-sizing", "inherit") }
        }

        // Controls do not inherit typography by default; declared here, no style repeats it.
        registerStyleBase("button, input, textarea, select") {
            Modifier.styleModifier {
                property("font-family", "inherit")
                property("font-size", "inherit")
                property("line-height", "inherit")
                property("color", "inherit")
            }
        }

        // Kobweb mounts the app on `#root`: without full height, the shell's `100vh` shrinks.
        registerStyleBase("#root") {
            Modifier.styleModifier {
                property("height", "100%")
                property("width", "100%")
            }
        }
    }

    registerKeyframes()
    registerReducedMotion()
}

/**
 * Neutralizes every animation and transition for whoever asked their system for less movement.
 *
 * Raw CSS, and that is the whole point: passing the media query to `registerStyleBase` as if it
 * were a selector compiles, runs, and emits `@media (prefers-reduced-motion: reduce) {}` —
 * empty. For months this guard did nothing: measured in the browser with the query emulated,
 * the hero kept its 7s float and the reveals their 620ms. The `!important` longhands are what
 * beat an `animation` shorthand declared on a class.
 */
private fun registerReducedMotion() {
    document.head?.appendChild(
        document.createElement("style").apply {
            textContent = """
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        /* Without capping repetition, an infinite 0.01ms animation flickers. */
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                    /* Not covered by the two above: an anchor jump is a scroll, not animation. */
                    html { scroll-behavior: auto !important }
                }
            """.trimIndent()
        },
    )
}


/**
 * The keyframes, injected as raw CSS.
 *
 * They do not go through the Silk API because the name that one generates is derived from the
 * property name: renaming the property would leave every stylesheet reference pointing at
 * nothing, with no error anywhere. The reduced-motion rule neutralizes all of them at once, and
 * covers any added later.
 *
 * `ha-glow` animates opacity and scale rather than a `box-shadow` color because a keyframe
 * written as raw text cannot read a token: what breathes is a blurred accent-filled element
 * behind the piece, and that element takes its color from the system like everything else.
 */
private fun registerKeyframes() {
    document.head?.appendChild(
        document.createElement("style").apply {
            textContent = """
                @keyframes ha-pulse { from { opacity: .45 } to { opacity: 1 } }
                @keyframes ha-sheen { from { transform: translateX(-100%) } to { transform: translateX(100%) } }
                @keyframes ha-sheen-counter { from { transform: translateX(100%) } to { transform: translateX(-100%) } }
                @keyframes ha-float {
                    0%, 100% { transform: translateY(0) rotate(0deg) }
                    50% { transform: translateY(-18px) rotate(-1.2deg) }
                }
                @keyframes ha-glow {
                    0%, 100% { opacity: .40; transform: scale(1) }
                    50% { opacity: .72; transform: scale(1.07) }
                }
                /*
                 * Opacity only, and no rise. The element this runs on wraps the whole page,
                 * the landing bar included, and a transformed ancestor kills that bar's
                 * `position: sticky`: it stops sticking to the viewport and scrolls away with
                 * the page. Measured here - with `transform: none` forced on the wrapper the
                 * bar holds at the top, with the 10px translate it does not. Ten pixels of
                 * rise are not worth a header that leaves.
                 */
                @keyframes ha-page-in {
                    from { opacity: 0 }
                    to { opacity: 1 }
                }
                @keyframes ha-brief-in {
                    from { opacity: 0 }
                    to { opacity: 1 }
                }
                /*
                 * The steps of the landing. The panel's element stays put from one step to
                 * the next, so an animation whose name repeats would not play again: the two
                 * panel sets are identical and the component alternates between them.
                 */
                @keyframes ha-step-panel-a {
                    from { opacity: 0; transform: translateX(var(--step-from, 48px)) }
                }
                @keyframes ha-step-panel-b {
                    from { opacity: 0; transform: translateX(var(--step-from, 48px)) }
                }
                @keyframes ha-step-body { from { opacity: 0; transform: translateY(10px) } }
                /* A bubble arriving: it rises and grows from the corner its tail points from. */
                @keyframes ha-bubble-in {
                    from { opacity: 0; transform: translateY(12px) scale(0.96) }
                    to { opacity: 1; transform: none }
                }
                @keyframes ha-brief-rise {
                    from { transform: translateY(8px) scale(0.99) }
                    to { transform: none }
                }
            """.trimIndent()
        },
    )

}

/**
 * Application root: mounts Silk, paints the color mode the preference resolves to, and puts the
 * texts of the language in force within reach of the whole tree.
 *
 * The system mode is listened to live: on auto, switching the phone to dark repaints the open
 * app without a reload.
 *
 * The `lang` of the document is set from the same effect: it is what makes a screen reader
 * pronounce the interface in the right language, and it has to move when the language does.
 */
@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    SilkApp {
        var colorMode by ColorMode.currentState
        val choice by ThemePreference.current.collectAsState()
        var system by remember { mutableStateOf(ColorMode.systemPreference) }
        DisposableEffect(Unit) {
            val query = window.matchMedia("(prefers-color-scheme: dark)")
            val listener: (dynamic) -> Unit = { system = if (query.matches) ColorMode.DARK else ColorMode.LIGHT }
            query.addEventListener("change", listener)
            onDispose { query.removeEventListener("change", listener) }
        }
        LaunchedEffect(choice, system) { colorMode = choice.resolve(system) }

        // The installed app's splash waits for this to leave; see `splash/splash.js`.
        LaunchedEffect(Unit) { js("window.halSplashDone && window.halSplashDone()") }

        val locale by AppLocale.current.collectAsState()
        LaunchedEffect(locale) { AppLocale.applyLang(locale) }

        CompositionLocalProvider(LocalStrings provides AppLocale.stringsFor(locale)) {
            Div(SmoothColorStyle.toModifier().toAttrs()) {
                content()
            }
        }
    }
}
